// Central cookie-consent store. Owns the consent cookies, exposes typed read/write helpers, and
// broadcasts changes via window events so non-React consumers (analytics in router.tsx) can react
// without prop-drilling. Banner UI lives in routes/_app/-components/shared/cookie-banner.tsx.
//
// Compliance notes (GDPR / ePrivacy / AEPD):
// - Non-necessary categories default to OFF; nothing is stored until the user makes a choice.
// - Rejecting is as easy as accepting (see the banner), and withdrawing consent deletes the
//   corresponding third-party cookies (clearAnalyticsCookies).
// - The consent cookies themselves are strictly necessary, so they are always allowed to be set.

import { deleteCookie, getCookie, setCookie } from './cookies'

// Cookie names kept stable from the previous implementation so existing visitors' choices and the
// e2e fixture (which seeds `co-banner-closed`) keep working.
export const CONSENT_COOKIE = {
  /** Set once the visitor has made any explicit choice — its presence hides the banner. */
  decided: 'co-banner-closed',
  statistics: 'co-statistics',
  marketing: 'co-marketing',
} as const

export interface CookieConsent {
  /** Whether the visitor has made an explicit accept/reject/save choice. */
  decided: boolean
  /** Always granted — necessary cookies can't be refused. Included so the shape is uniform. */
  necessary: true
  statistics: boolean
  marketing: boolean
}

export type ConsentCategories = Pick<CookieConsent, 'statistics' | 'marketing'>

// 180 days: long enough not to nag, short enough to re-ask within a reasonable window.
const MAX_AGE_SECONDS = 60 * 60 * 24 * 180

// Fired (on window) whenever consent is written. detail is the fresh CookieConsent.
export const CONSENT_CHANGE_EVENT = 'co:consent-change'
// Fired (on window) to ask the banner to open its preferences dialog from anywhere (e.g. footer).
export const OPEN_PREFERENCES_EVENT = 'co:open-preferences'

export function readConsent(): CookieConsent {
  return {
    decided: getCookie(CONSENT_COOKIE.decided) === 'true',
    necessary: true,
    statistics: getCookie(CONSENT_COOKIE.statistics) === 'true',
    marketing: getCookie(CONSENT_COOKIE.marketing) === 'true',
  }
}

/** Best-effort removal of Google Analytics cookies when statistics consent is withdrawn. */
export function clearAnalyticsCookies(): void {
  if (typeof document === 'undefined') return
  // GA writes _ga, _gid, _gat and per-property _ga_<ID>. Match the whole family by prefix.
  const gaNames = document.cookie
    .split(';')
    .map((c) => c.split('=')[0]?.trim())
    .filter(
      (n): n is string => n !== undefined && (n.startsWith('_ga') || n.startsWith('_gid') || n.startsWith('_gat')),
    )

  // GA typically sets cookies on the registrable domain (".danchiano.com"), so we have to try the
  // dotted parent domain too — deleting only on the exact host leaves the original cookie in place.
  const host = typeof location !== 'undefined' ? location.hostname : ''
  const parentDomain = host.split('.').slice(-2).join('.')
  for (const name of gaNames) {
    deleteCookie(name)
    if (host) deleteCookie(name, { domain: host })
    if (parentDomain && parentDomain !== host) deleteCookie(name, { domain: `.${parentDomain}` })
  }
}

/** Persist a consent decision, clean up withdrawn cookies, and notify listeners. */
export function writeConsent(categories: ConsentCategories): void {
  setCookie(CONSENT_COOKIE.statistics, String(categories.statistics), { maxAge: MAX_AGE_SECONDS })
  setCookie(CONSENT_COOKIE.marketing, String(categories.marketing), { maxAge: MAX_AGE_SECONDS })
  setCookie(CONSENT_COOKIE.decided, 'true', { maxAge: MAX_AGE_SECONDS })

  if (!categories.statistics) clearAnalyticsCookies()

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<CookieConsent>(CONSENT_CHANGE_EVENT, { detail: readConsent() }))
  }
}

/** Open the cookie-preferences dialog from anywhere in the app (footer link, etc.). */
export function openCookiePreferences(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(OPEN_PREFERENCES_EVENT))
}
